import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { updateBookingRecord } from '../../../redux/slices/bookingsLogSlice';
import useJobNumberLogic from '../../../functions/useJobNumberLogic';

const JobNumberAssignmentModal = ({ show, onHide, job, onSave }) => {
  const dispatch = useDispatch();
  const { 
    getNextJobNumberWithFreshData,
    getRecentJobNumbersWithFreshData,
    validateJobNumberWithFreshData,
    getJobNumbersByYearWithFreshData
  } = useJobNumberLogic();

  const [jobNumber, setJobNumber] = useState('');
  const [validation, setValidation] = useState({ isValid: true, errors: [], warnings: [] });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [freshData, setFreshData] = useState({
    suggestedJobNumber: '',
    recentJobNumbers: [],
    currentYearNumbers: []
  });

  const currentYear = new Date().getFullYear();

  // Fetch fresh data when modal opens
  useEffect(() => {
    if (show && job) {
      setLoading(true);
      Promise.all([
        getNextJobNumberWithFreshData(currentYear),
        getRecentJobNumbersWithFreshData(10),
        getJobNumbersByYearWithFreshData(currentYear)
      ]).then(([suggestedNumber, recentNumbers, yearNumbers]) => {
        setFreshData({
          suggestedJobNumber: suggestedNumber,
          recentJobNumbers: recentNumbers,
          currentYearNumbers: yearNumbers
        });
        setLoading(false);
      }).catch((error) => {
        console.error('Failed to fetch fresh job number data:', error);
        setLoading(false);
      });
    }
  }, [show, job, currentYear, getNextJobNumberWithFreshData, getRecentJobNumbersWithFreshData, getJobNumbersByYearWithFreshData]);

  // Initialize job number when modal opens or fresh data loads
  useEffect(() => {
    if (show && job && freshData.suggestedJobNumber) {
      if (job.JobNumber && job.JobNumber.trim()) {
        setJobNumber(job.JobNumber);
      } else {
        setJobNumber(freshData.suggestedJobNumber);
      }
    }
  }, [show, job, freshData.suggestedJobNumber]);

  // Validate job number when it changes
  useEffect(() => {
    if (jobNumber && !loading) {
      validateJobNumberWithFreshData(jobNumber).then((validationResult) => {
        setValidation(validationResult);
      }).catch((error) => {
        console.error('Failed to validate job number:', error);
        setValidation({ isValid: false, errors: ['Failed to validate job number'], warnings: [] });
      });
    }
  }, [jobNumber, loading, validateJobNumberWithFreshData]);

  const handleSave = async () => {
    if (!validation.isValid) return;

    setSaving(true);
    try {
      // Update the job number using the existing update system
      await dispatch(updateBookingRecord({
        table: 'generalProject', // JobNumber belongs to generalProject table
        id: job.ProjectID,
        field: 'JobNumber',
        value: jobNumber,
        projectId: job.ProjectID
      })).unwrap();

      if (onSave) {
        onSave(jobNumber);
      }
      onHide();
    } catch (error) {
      console.error('Failed to update job number:', error);
      setValidation(prev => ({
        ...prev,
        errors: [...prev.errors, 'Failed to save job number. Please try again.']
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setJobNumber('');
    setValidation({ isValid: true, errors: [], warnings: [] });
    onHide();
  };

  const formatJobNumberDisplay = (jobNum) => {
    if (jobNum.includes('-')) {
      const [base, child] = jobNum.split('-');
      return (
        <>
          {base}<span style={{ color: '#6c757d' }}>-{child}</span>
        </>
      );
    }
    return jobNum;
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" dialogClassName="apps-responsive-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          Assign Job Number
          {job && (
            <small className="text-muted d-block">
              Project: {job.ProjectName || 'Unnamed Project'} (ID: {job.ProjectID})
            </small>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Job Number Summary */}
        <div className="mb-3 p-3 bg-light rounded">
          <div className="row">
            <div className="col-md-6">
              <strong>Last job number: </strong>
              {freshData.currentYearNumbers.length > 0 ? (
                <code>{formatJobNumberDisplay(freshData.currentYearNumbers[0].original)}</code>
              ) : (
                <span className="text-muted">None for {currentYear}</span>
              )}
            </div>
            <div className="col-md-6">
              <strong>Suggested next: </strong>
              <code>{freshData.suggestedJobNumber}</code>
            </div>
          </div>
        </div>

        {/* Job Number Input */}
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Job Number</strong>
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2"
              onClick={() => setJobNumber(freshData.suggestedJobNumber)}
              title="Use suggested number"
            >
              Use Suggested
            </Button>
          </Form.Label>
          <Form.Control
            type="text"
            value={jobNumber}
            onChange={(e) => setJobNumber(e.target.value.trim())}
            isInvalid={!validation.isValid}
            placeholder="Enter job number (e.g., 2025001)"
            autoFocus
          />
          <Form.Control.Feedback type="invalid">
            {validation.errors.join(', ')}
          </Form.Control.Feedback>
          {validation.warnings.length > 0 && (
            <div className="text-warning small mt-1">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {validation.warnings.join(', ')}
            </div>
          )}
          <Form.Text className="text-muted">
            Format: YYYY### (e.g., {freshData.suggestedJobNumber}). Use -00 for primary, -01, -02, etc. for child numbers.
          </Form.Text>
        </Form.Group>

        {/* Recent Job Numbers Table */}
        <div className="mb-3">
          <h6>Recent Job Numbers</h6>
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Job Number</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {freshData.recentJobNumbers.length > 0 ? (
                  freshData.recentJobNumbers.map((job, index) => (
                    <tr key={index}>
                      <td>
                        <code>{formatJobNumberDisplay(job.original)}</code>
                      </td>
                      <td>
                        {job.isChildNumber ? (
                          <Badge bg="info">Child</Badge>
                        ) : (
                          <Badge bg="success">Primary</Badge>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="text-muted text-center">
                      No job numbers found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave} 
          disabled={!validation.isValid || saving}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            'Save Job Number'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default JobNumberAssignmentModal;
